import { APEX25_INDUSTRY_PAGES, APEX25_LEGAL_PAGES } from "@/pages/apex25/industries";

// The Apex 25 marketing hosts are served by this same app, but they are pure marketing pages: no
// Clerk, no sign-in (Clerk's production instance rejects tryapex25.com outright). They show the
// Apex 25 page at "/" plus the industry pages, and every link into the app (sign-in, dashboard)
// goes to recruit.theresume.io. Everywhere else (recruit.theresume.io, staging, localhost) links
// stay relative, so each environment keeps linking to itself.
// Keep in sync with APEX25_SITE.hosts in apps/server/src/brand-sites/brand-sites.ts.
const APEX25_MARKETING_HOSTS = new Set([
  "tryapex25.com",
  "www.tryapex25.com",
  "apex25.theresume.io",
]);
const THERECRUIT_APP_ORIGIN = "https://recruit.theresume.io";
export const APEX25_ORIGIN = "https://tryapex25.com";

/** Query value that makes the sign-in / sign-up screen say "Apex 25, powered by theResume". */
export const APEX25_BRAND_PARAM = "apex25";

const currentHostname = () => (typeof window === "undefined" ? "" : window.location.hostname);

export function isApex25MarketingHost(hostname = currentHostname()): boolean {
  return APEX25_MARKETING_HOSTS.has(hostname);
}

/** Tag sign-in / sign-up links with ?brand=apex25 so the auth screen shows the Apex 25 line. */
function withBrand(path: string): string {
  if (!/^\/sign-(in|up)(\/|\?|$)/.test(path) || /[?&]brand=/.test(path)) return path;
  return `${path}${path.includes("?") ? "&" : "?"}brand=${APEX25_BRAND_PARAM}`;
}

export function toApex25AppUrl(path: string, hostname = currentHostname()): string {
  const branded = withBrand(path);
  return isApex25MarketingHost(hostname) ? `${THERECRUIT_APP_ORIGIN}${branded}` : branded;
}

/**
 * Apex 25's home for the current environment: the brand domain in production, the in-app
 * marketing route on staging and localhost, which have no tryapex25.com deployment. Used by the
 * sign-in screen's "Back home" link so an Apex 25 visitor goes back where they came from instead
 * of landing on theRecruit's homepage.
 */
export function apex25HomeUrl(hostname = currentHostname()): string {
  return hostname === "recruit.theresume.io" || isApex25MarketingHost(hostname)
    ? `${APEX25_ORIGIN}/`
    : "/apex25";
}

/** Whether a link target is one of the Apex 25 legal pages (/legal, /terms, /privacy...). */
export function isApex25LegalPath(target: string): boolean {
  return target in APEX25_LEGAL_PAGES;
}

/**
 * An Apex 25 legal page for the current environment: the brand domain in production (relative on
 * the marketing hosts, absolute from recruit.theresume.io, e.g. the sign-in screen), the in-app
 * copy at /apex25<path> on staging and localhost. Append a "#section-N" anchor as needed. The same
 * documents cover theRecruit, so theRecruit's own footer and sign-in link here too.
 */
export function apex25LegalUrl(path: string, hostname = currentHostname()): string {
  if (isApex25MarketingHost(hostname)) return path;
  return hostname === "recruit.theresume.io" ? `${APEX25_ORIGIN}${path}` : `/apex25${path}`;
}

/**
 * Pages that moved from recruit.theresume.io to tryapex25.com. The server 301s full page loads;
 * this covers in-app navigation to the old paths. Only production recruit.theresume.io redirects,
 * so staging and localhost keep serving the pages.
 */
const MOVED_TO_APEX25: Record<string, string> = {
  "/apex25": "/",
  ...Object.fromEntries(Object.keys(APEX25_LEGAL_PAGES).map((path) => [`/apex25${path}`, path])),
  ...Object.fromEntries(Object.keys(APEX25_INDUSTRY_PAGES).map((path) => [path, path])),
};

export function movedToApex25Url(pathname: string, hostname = currentHostname()) {
  if (hostname !== "recruit.theresume.io") return undefined;
  const target = MOVED_TO_APEX25[pathname.replace(/\/+$/, "") || "/"];
  return target === undefined ? undefined : `${APEX25_ORIGIN}${target}`;
}

const DEFAULT_DEMO_BOOKING_URL = "https://cal.com/tryapex25/30min";

/**
 * The "Book a demo" link (cal.com, set by VITE_DEMO_BOOKING_URL at build time). Carries the
 * visitor's utm_* parameters from the current page so the booking keeps its campaign source, and,
 * when we know who they are (signed in), pre-fills cal.com's name and email fields.
 */
export function demoBookingUrl(
  search = typeof window === "undefined" ? "" : window.location.search,
  prefill?: { name?: string; email?: string }
) {
  const configured = import.meta.env.VITE_DEMO_BOOKING_URL?.trim() || DEFAULT_DEMO_BOOKING_URL;
  const url = new URL(configured);
  new URLSearchParams(search).forEach((value, key) => {
    if (key.startsWith("utm_")) url.searchParams.set(key, value);
  });
  if (prefill?.name) url.searchParams.set("name", prefill.name);
  if (prefill?.email) url.searchParams.set("email", prefill.email);
  return url.toString();
}

/** Where the demo results live: behind sign-in on theRecruit's app host. */
export const demoResultsPath = (sessionId: string) => `/demo/interview/${sessionId}`;

/**
 * Link from the demo on tryapex25.com to its results on recruit.theresume.io. `auth` picks which
 * screen a signed-out visitor sees first. Relative everywhere except the marketing hosts, so
 * staging and localhost keep the whole flow in one environment. Use it on a real <a>: the
 * handoff decorator adds first-touch attribution (`ft`) to app links as they are clicked.
 */
export function demoResultsUrl(
  sessionId: string,
  auth: "signup" | "signin",
  hostname = currentHostname()
) {
  const path = `${demoResultsPath(sessionId)}?brand=${APEX25_BRAND_PARAM}&auth=${auth}`;
  return isApex25MarketingHost(hostname) ? `${THERECRUIT_APP_ORIGIN}${path}` : path;
}
